import { useState, useEffect } from 'react';
import { collection, query, where, getDocs, orderBy, doc, getDoc } from 'firebase/firestore';
import { getDb } from '../../firebase/utils';
import { LeaveRequest, LeaveRequestStatus } from '../types/leaveRequest';
import { useCurrentUser } from './useCurrentUser';

/**
 * 내 휴가 신청 목록 조회
 */
export function useMyLeaveRequests(status?: LeaveRequestStatus) {
  const { user } = useCurrentUser();
  const [requests, setRequests] = useState<LeaveRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!user?.profile?.id) {
      setIsLoading(false);
      return;
    }

    const fetchRequests = async () => {
      try {
        setIsLoading(true);
        setError(null);

        let q = query(
          collection(getDb(), 'leaveRequests'),
          where('employeeId', '==', user.profile.id),
          orderBy('createdAt', 'desc')
        );

        if (status) {
          q = query(q, where('status', '==', status));
        }

        const snapshot = await getDocs(q);
        const requestList = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        })) as LeaveRequest[];

        setRequests(requestList);
      } catch (err) {
        console.error('휴가 신청 목록 조회 실패:', err);
        setError(err as Error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRequests();
  }, [user?.profile?.id, status]);

  return { requests, isLoading, error };
}

/**
 * 팀원 휴가 신청 목록 조회 (조직장용)
 */
export function useTeamLeaveRequests(orgUnitId?: string, status?: LeaveRequestStatus) {
  const [requests, setRequests] = useState<LeaveRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!orgUnitId) {
      setIsLoading(false);
      return;
    }

    const fetchRequests = async () => {
      try {
        setIsLoading(true);
        setError(null);

        let q = query(
          collection(getDb(), 'leaveRequests'),
          where('orgUnitId', '==', orgUnitId),
          orderBy('createdAt', 'desc')
        );

        if (status) {
          q = query(q, where('status', '==', status));
        }

        const snapshot = await getDocs(q);
        const requestList = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        })) as LeaveRequest[];

        setRequests(requestList);
      } catch (err) {
        console.error('팀 휴가 신청 목록 조회 실패:', err);
        setError(err as Error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRequests();
  }, [orgUnitId, status]);

  return { requests, isLoading, error };
}

/**
 * 전체 휴가 신청 목록 조회 (HR용)
 */
export function useAllLeaveRequests(status?: LeaveRequestStatus) {
  const [requests, setRequests] = useState<LeaveRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchRequests = async () => {
      try {
        setIsLoading(true);
        setError(null);

        let q = query(
          collection(getDb(), 'leaveRequests'),
          orderBy('createdAt', 'desc')
        );

        if (status) {
          q = query(q, where('status', '==', status));
        }

        const snapshot = await getDocs(q);
        const requestList = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        })) as LeaveRequest[];

        setRequests(requestList);
      } catch (err) {
        console.error('전체 휴가 신청 목록 조회 실패:', err);
        setError(err as Error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRequests();
  }, [status]);

  return { requests, isLoading, error };
}

/**
 * 특정 휴가 신청 조회
 */
export function useLeaveRequest(requestId?: string) {
  const [request, setRequest] = useState<LeaveRequest | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!requestId) {
      setIsLoading(false);
      return;
    }

    const fetchRequest = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const docRef = doc(getDb(), 'leaveRequests', requestId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          setRequest({ id: docSnap.id, ...docSnap.data() } as LeaveRequest);
        } else {
          setRequest(null);
        }
      } catch (err) {
        console.error('휴가 신청 조회 실패:', err);
        setError(err as Error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRequest();
  }, [requestId]);

  return { request, isLoading, error };
}

/**
 * 승인 대기 중인 휴가 신청 목록 조회 (승인자용)
 */
export function usePendingLeaveApprovals(approverId?: string, approverRole?: 'org' | 'hr') {
  const [requests, setRequests] = useState<LeaveRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!approverId) {
      setIsLoading(false);
      return;
    }

    const fetchRequests = async () => {
      try {
        setIsLoading(true);
        setError(null);

        let q;

        if (approverRole === 'org') {
          q = query(
            collection(getDb(), 'leaveRequests'),
            where('orgApproverId', '==', approverId),
            where('status', '==', 'pending'),
            orderBy('createdAt', 'desc')
          );
        } else if (approverRole === 'hr') {
          q = query(
            collection(getDb(), 'leaveRequests'),
            where('hrApproverId', '==', approverId),
            where('status', '==', 'orgApproved'),
            orderBy('createdAt', 'desc')
          );
        } else {
          // 모든 승인 대기 건
          q = query(
            collection(getDb(), 'leaveRequests'),
            where('status', 'in', ['pending', 'orgApproved']),
            orderBy('createdAt', 'desc')
          );
        }

        const snapshot = await getDocs(q);
        const requestList = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        })) as LeaveRequest[];

        setRequests(requestList);
      } catch (err) {
        console.error('승인 대기 휴가 조회 실패:', err);
        setError(err as Error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRequests();
  }, [approverId, approverRole]);

  return { requests, isLoading, error };
}
